import { mobileShopApi } from '../mobileShopApi';
import { MasterRow, PagedResponse } from '../../types/master';
import { Bill } from '../../types/bill';
import {
  BillReceipt,
  BrandSalesRow,
  Collections,
  DiscountLine,
  DashboardData,
  DeadStockRow,
  EmiReportRow,
  GstRegisterRow,
  GstSummary,
  LowStockRow,
  OpenInvoice,
  OutstandingSummary,
  PeriodTotals,
  PurchasePayment,
  ReceivePayment,
  StaffReport,
  StaffRow,
  SupplierDue,
  TopProduct,
} from '../../types/reports';

const dateParams = (from?: string, to?: string) => {
  const parts: string[] = [];
  if (from) parts.push(`fromDate=${from}`);
  if (to) parts.push(`toDate=${to}`);
  return parts;
};

export const reportApi = mobileShopApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrandWiseReport: builder.query<
      BrandSalesRow[],
      { fromDate?: string; toDate?: string; brandId?: number | null; tag?: string }
    >({
      query: ({ fromDate, toDate, brandId, tag }) => {
        const parts = dateParams(fromDate, toDate);
        if (brandId) parts.push(`brandId=${brandId}`);
        if (tag) parts.push(`tag=${encodeURIComponent(tag)}`);
        return `/report/brand-wise${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Imei', 'Bill'],
    }),

    getEmiReport: builder.query<
      EmiReportRow[],
      { fromDate?: string; toDate?: string; bankId?: number | null; settled?: boolean | null; tag?: string }
    >({
      query: ({ fromDate, toDate, bankId, settled, tag }) => {
        const parts = dateParams(fromDate, toDate);
        if (bankId) parts.push(`bankId=${bankId}`);
        if (settled !== null && settled !== undefined) parts.push(`settled=${settled}`);
        if (tag) parts.push(`tag=${encodeURIComponent(tag)}`);
        return `/report/emi${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Bill'],
    }),

    /** The owner's first screen, in one call. */
    getDashboard: builder.query<DashboardData, void>({
      query: () => '/report/dashboard',
      providesTags: ['Bill', 'Imei', 'Purchase'],
    }),

    /**
     * Sales, bills, profit and average bill for any range - what the dashboard needs
     * when the owner picks this week or a single date rather than one of the three
     * periods that come free with /dashboard.
     */
    getPeriodTotals: builder.query<PeriodTotals, { fromDate?: string; toDate?: string }>({
      query: ({ fromDate, toDate }) => {
        const parts = dateParams(fromDate, toDate);
        return `/report/period-totals${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Bill'],
    }),

    /**
     * Best sellers over the same range as the rest of the dashboard, so the Top
     * Selling panel answers the period the owner picked rather than always the month.
     */
    getTopProducts: builder.query<TopProduct[], { fromDate?: string; toDate?: string }>({
      query: ({ fromDate, toDate }) => {
        const parts = dateParams(fromDate, toDate);
        return `/report/top-products${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Bill'],
    }),

    /**
     * The GST register, paged: one row per bill with its value, rate and tax. Totals
     * cover the whole period rather than the page.
     */
    getGstRegisterPage: builder.mutation<
      PagedResponse<GstRegisterRow> & {
        billAmount: number;
        cgstAmount: number;
        sgstAmount: number;
        igstAmount: number;
        gstAmount: number;
        netAmount: number;
      },
      { searchValue: string; fromDate?: string; toDate?: string; page: number; size: number }
    >({
      query: ({ searchValue, fromDate, toDate, page, size }) => ({
        url: `/report/gst-register/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, fromDate, toDate },
      }),
    }),

    /** The day book: billed by payment type, collected, and cash in hand. */
    getCollections: builder.query<Collections, { fromDate?: string; toDate?: string }>({
      query: ({ fromDate, toDate }) => {
        const parts = dateParams(fromDate, toDate);
        return `/report/collections${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Bill'],
    }),

    getLowStock: builder.query<LowStockRow[], { level: number; tag?: string }>({
      query: ({ level, tag }) => `/report/low-stock?level=${level}&tag=${encodeURIComponent(tag ?? '')}`,
      providesTags: ['Imei', 'Product'],
    }),

    getDeadStock: builder.query<DeadStockRow[], { days: number; tag?: string }>({
      query: ({ days, tag }) => `/report/dead-stock?days=${days}&tag=${encodeURIComponent(tag ?? '')}`,
      providesTags: ['Batch', 'Imei'],
    }),

    /** Who sold what, and what they gave away doing it. */
    getStaffReport: builder.query<StaffReport, { fromDate?: string; toDate?: string; tag?: string }>({
      query: ({ fromDate, toDate, tag }) => {
        const parts = dateParams(fromDate, toDate);
        if (tag) parts.push(`tag=${encodeURIComponent(tag)}`);
        return `/report/staff${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Bill'],
    }),

    getGstSummary: builder.query<GstSummary, { fromDate?: string; toDate?: string }>({
      query: ({ fromDate, toDate }) => {
        const parts = dateParams(fromDate, toDate);
        return `/report/gst${parts.length ? `?${parts.join('&')}` : ''}`;
      },
      providesTags: ['Bill'],
    }),

    /* what the shop owes its suppliers */

    getSupplierDues: builder.query<SupplierDue[], string>({
      query: (tag) => `/purchase-payment/dues?tag=${encodeURIComponent(tag ?? '')}`,
      providesTags: ['Purchase'],
    }),

    getOpenInvoices: builder.query<OpenInvoice[], number>({
      query: (supplierId) => `/purchase-payment/supplier/${supplierId}/open`,
      providesTags: ['Purchase'],
    }),

    getPurchasePayments: builder.query<PurchasePayment[], number>({
      query: (purchaseId) => `/purchase-payment/purchase/${purchaseId}`,
      providesTags: ['Purchase'],
    }),

    paySupplier: builder.mutation<PurchasePayment, PurchasePayment>({
      query: (body) => ({ url: '/purchase-payment', method: 'POST', body }),
      invalidatesTags: ['Purchase'],
    }),

    /* ------------------------------------------------------------------ paged
     * Every list screen in the app uses the same grid, which needs a total and one
     * page of rows. These are the paged twins of the reports above.
     */

    getBrandWisePage: builder.mutation<
      PagedResponse<BrandSalesRow> & {
        boughtQuantity: number;
        boughtValue: number;
        soldQuantity: number;
        salesValue: number;
        remainingQuantity: number;
        remainingValue: number;
        profit: number;
      },
      { searchValue: string; brandId?: number | null; fromDate?: string; toDate?: string; page: number; size: number }
    >({
      query: ({ searchValue, brandId, fromDate, toDate, page, size }) => ({
        url: `/report/brand-wise/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, brandId, fromDate, toDate },
      }),
    }),

    getOutstandingPage: builder.mutation<
      PagedResponse<OutstandingSummary> & { totalDue: number; totalBills: number },
      { searchValue: string; page: number; size: number }
    >({
      query: ({ searchValue, page, size }) => ({
        url: `/outstanding/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue },
      }),
    }),

    getSupplierDuesPage: builder.mutation<
      PagedResponse<SupplierDue> & {
        totalDue: number;
        totalBilled: number;
        totalPaid: number;
        invoiceCount: number;
      },
      { searchValue: string; onlyDue: boolean; page: number; size: number }
    >({
      query: ({ searchValue, onlyDue, page, size }) => ({
        url: `/purchase-payment/dues/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, onlyDue },
      }),
    }),

    getLowStockPage: builder.mutation<PagedResponse<LowStockRow>, { searchValue: string; level: number; page: number; size: number }>({
      query: ({ searchValue, level, page, size }) => ({
        url: `/report/low-stock/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, level },
      }),
    }),

    getDeadStockPage: builder.mutation<PagedResponse<DeadStockRow>, { searchValue: string; days: number; page: number; size: number }>({
      query: ({ searchValue, days, page, size }) => ({
        url: `/report/dead-stock/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, days },
      }),
    }),

    getEmiPage: builder.mutation<
      PagedResponse<EmiReportRow> & { dueFromBanks: number },
      {
        searchValue: string;
        bankId?: number | null;
        settled?: boolean | null;
        fromDate?: string;
        toDate?: string;
        page: number;
        size: number;
      }
    >({
      query: ({ searchValue, bankId, settled, fromDate, toDate, page, size }) => ({
        url: `/report/emi/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, bankId, settled, fromDate, toDate },
      }),
    }),

    getStaffPage: builder.mutation<
      PagedResponse<DiscountLine> & { rows: StaffRow[]; lineCount: number },
      { searchValue: string; fromDate?: string; toDate?: string; page: number; size: number }
    >({
      query: ({ searchValue, fromDate, toDate, page, size }) => ({
        url: `/report/staff/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, fromDate, toDate },
      }),
    }),

    /** Ticked when the finance company's payout reaches the shop. */
    markEmiSettled: builder.mutation<EmiReportRow, { billId: number; settled: boolean; settledDate?: string }>({
      query: ({ billId, settled, settledDate }) => ({
        url: `/report/emi/${billId}/settled`,
        method: 'PUT',
        body: { settled, settledDate },
      }),
      invalidatesTags: ['Bill'],
    }),

    /** Stock list and serial number search, paged. */
    getStockPage: builder.mutation<PagedResponse<MasterRow>, { searchValue: string; status: string; page: number; size: number }>({
      query: ({ searchValue, status, page, size }) => ({
        url: `/imei-stock/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, status },
      }),
    }),

    /* credit outstanding */

    getOutstanding: builder.query<OutstandingSummary[], string>({
      query: (tag) => `/outstanding?tag=${encodeURIComponent(tag)}`,
      providesTags: ['Bill'],
    }),

    getOpenBills: builder.query<Bill[], { customerName?: string; mobileNumber?: string }>({
      query: ({ customerName = '', mobileNumber = '' }) =>
        `/outstanding/bills?customerName=${encodeURIComponent(customerName)}&mobileNumber=${encodeURIComponent(mobileNumber)}`,
      providesTags: ['Bill'],
    }),

    getPaymentHistory: builder.query<ReceivePayment[], number>({
      query: (billId) => `/outstanding/history/${billId}`,
      providesTags: ['Bill'],
    }),

    receivePayment: builder.mutation<ReceivePayment, ReceivePayment>({
      query: (body) => ({ url: '/outstanding/receive', method: 'POST', body }),
      invalidatesTags: ['Bill'],
    }),

    /**
     * One receipt paid in several ways at once. Every line comes back carrying the
     * bill's position after the whole receipt, so the screen reads the last one.
     */
    receiveSplitPayment: builder.mutation<ReceivePayment[], BillReceipt>({
      query: (body) => ({ url: '/outstanding/receive-split', method: 'POST', body }),
      invalidatesTags: ['Bill'],
    }),
  }),
});

export const {
  useGetBrandWisePageMutation,
  useGetOutstandingPageMutation,
  useGetSupplierDuesPageMutation,
  useGetLowStockPageMutation,
  useGetDeadStockPageMutation,
  useGetEmiPageMutation,
  useGetStaffPageMutation,
  useGetDashboardQuery,
  useGetCollectionsQuery,
  useGetPeriodTotalsQuery,
  useGetTopProductsQuery,
  useGetLowStockQuery,
  useGetDeadStockQuery,
  useGetGstSummaryQuery,
  useGetGstRegisterPageMutation,
  useGetStaffReportQuery,
  useGetSupplierDuesQuery,
  useLazyGetOpenInvoicesQuery,
  useLazyGetPurchasePaymentsQuery,
  usePaySupplierMutation,
  useGetBrandWiseReportQuery,
  useGetEmiReportQuery,
  useMarkEmiSettledMutation,
  useGetStockPageMutation,
  useGetOutstandingQuery,
  useLazyGetOpenBillsQuery,
  useLazyGetPaymentHistoryQuery,
  useReceivePaymentMutation,
  useReceiveSplitPaymentMutation,
} = reportApi;
